import { Component, OnInit, Renderer2 } from '@angular/core';
import { Globals } from '@app/shared/models';
import { UserData, selectors, FetchLoggedInUser } from '@app/core/features/auth';
import { MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormControlName, FormGroup } from '@angular/forms';
import { UpdateUserData } from '@app/pages/my-account/my-account.models';
import { ProfileService } from '@app/pages/my-account/my-account.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { Store } from '@ngrx/store';
import { State } from '@app/core/features';
import { takeUntil } from 'rxjs/operators';
import { BaseComponent } from '@app/shared/components/base/base/base.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-edit-account-details',
  templateUrl: './edit-account-details.component.html',
  styleUrls: ['./edit-account-details.component.scss']
})
export class EditAccountDetailsComponent extends BaseComponent implements OnInit {
  userData: UserData;
  isFetching: boolean;
  profileForm: FormGroup;
  requiredFields = [
    'firstName',
    'lastName'
  ];

  constructor(
    public dialogRef: MatDialogRef<EditAccountDetailsComponent>,
    public globals: Globals,
    private profileService: ProfileService,
    private toastr: ToastrService,
    private store: Store<State>,
    private formBuilder: FormBuilder,
    private renderer: Renderer2,
    public translateService: TranslateService,
  ) {
    super();

  }

  ngOnInit(): void {
    this.store
      .pipe(selectors.getUserData)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((result: UserData) => {
        if (result !== null) {
          this.userData = result;
          this.initForm();
        }
      });
  }

  public close() {
    this.dialogRef.close();
  }

  initForm() {
    const { firstName, lastName, phoneNumber, email } = this.userData;
    this.profileForm = this.formBuilder.group({
      firstName,
      lastName,
      phoneNumber,
      email,
    });

    const originFormControlNameNgOnChanges = FormControlName.prototype.ngOnChanges;
    FormControlName.prototype.ngOnChanges = function () {
      const result = originFormControlNameNgOnChanges.apply(this, arguments);
      this.control.nativeElement = this.valueAccessor._elementRef.nativeElement;
      return result;
    };
  }

  saveProfile() {
    if (this.checkInvalidEditProfileForm() === true)
      return;
    this.isFetching = true;
    this.resetControlErrors()
    const payload: UpdateUserData = {
      id: this.userData.id,
      givenName: this.profileForm.value.firstName,
      surName: this.profileForm.value.lastName,
      phoneNumber: this.assignEmptyAttribute(this.profileForm.value.phoneNumber),
      emailAddress: this.profileForm.value.emailAddress,
      city: '',
      state: '',
      country: '',
      streetAddress: '',
      streetAddress2: '',
      postalCode: '',
    };
    this.profileService.updateProfile(payload).subscribe(async (response: any) => {
      if (response instanceof HttpErrorResponse) {
        var message = '';
        if (response.error) {
          message = response.error.Message;
        } else {
          message = response.message;
        }
        this.toastr.error(message, 'ERROR');
        this.isFetching = false;
      } else {
        this.store.dispatch(new FetchLoggedInUser());
        this.isFetching = false;
        this.dialogRef.close();
      }
    });
  }

  assignEmptyAttribute(value: string) {
    return value?.trim() === '' ? ' ' : value;
  }

  clearEmptyAttribute(value: string) {
    return value === ' ' ? '' : value;
  }

  private checkInvalidEditProfileForm() {
    this.resetControlErrors();

    if (this.profileForm.invalid) {
      this.checkError();
      return true;
    }
  }

  private checkError() {
    switch ('') {
      case this.profileForm.value.firstName:
        this.renderer.addClass(this.getControlNativeElement('firstName'), 'form-error')

        this.translateService.get('MY_ACCOUNT.PERSONAL_INFO.FIRST_NAME').subscribe((name) => {
          this.translateService.get('PAYMENT_CART.CREDIT_CARD.VALIDATE_REQUIRE', { name }).subscribe((value) => {
            this.toastr.error(value);
          });
        });
        break;

      case this.profileForm.value.lastName:
        this.renderer.addClass(this.getControlNativeElement('lastName'), 'form-error')

        this.translateService.get('MY_ACCOUNT.PERSONAL_INFO.LAST_NAME').subscribe((name) => {
          this.translateService.get('PAYMENT_CART.CREDIT_CARD.VALIDATE_REQUIRE', { name }).subscribe((value) => {
            this.toastr.error(value);
          });
        });
        break;
    }
  }

  private resetControlErrors() {
    for (let i = 0; i < this.requiredFields.length; i++) {
      const fieldName = this.requiredFields[i];

      this.renderer.removeClass(this.getControlNativeElement(fieldName), 'form-error')
    }
  }

  private getControlNativeElement(controlName: string) {
    return (<any>this.profileForm.controls[controlName]).nativeElement.parentElement
  }
}
